// utils/formThumbnailResolver.js
/*  ======================================================================
    Resolve screenshot URLs for a list of form docs
    ----------------------------------------------------------------------
    • NEW behaviour – short‑circuit when the form doc already carries
      a usable URL (`screenshotUrl` or `versions.thumbnail.url`).

    • BACKWARD‑COMPAT  – if the URL is missing (old documents) we fall
      back to Firestore ‑> assets lookup exactly as before.

    • organisationId: 
         – pass the real orgId for org‑scope templates
         – pass the string "global"   for global templates
           (keeps TemplateGallery unchanged)
   ====================================================================== */

import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

const isBrowserUrl = (value) => (
  typeof value === 'string' &&
  /^https?:\/\//i.test(value)
);

const isStorageAssetPath = (value) => (
  typeof value === 'string' &&
  value.startsWith('assets/')
);

const pickScreenshotRefs = (form) => {
  const thumb =
    form?.screenshotThumbnail
    ?? form?.thumb?.urls?.['400']
    ?? form?.thumb?.urls?.['800']
    ?? form?.versions?.thumbnail?.path
    ?? form?.versions?.thumbnail?.url
    ?? null;

  const full =
    form?.screenshotPath
    ?? form?.versions?.original?.path
    ?? form?.versions?.original?.url
    ?? null;

  const legacy =
    form?.screenshotUrl
    ?? null;

  const directRef =
    thumb
    ?? full
    ?? legacy
    ?? null;

  return {
    thumb,
    full,
    legacy,
    directRef,
    hasBrowserUrl: isBrowserUrl(directRef),
    hasStoragePath: isStorageAssetPath(directRef),
  };
};

/**
 * @param {Array<Object>} forms         Array of form docs (must include `id`)
 * @param {string}        organizationId Real orgId  *or*  the literal `"global"`
 * @returns {Promise<Array<Object>>}     Same list, each with `screenshotUrl`
 */
export const resolveScreenshotUrlsForForms = async (
  forms = [],
  organizationId = '',
) => {
  if (!forms.length) return forms;          // nothing to do

  /* ──────────────────────────────────────────────────────────────
     1.  FIRST PASS  – keep any URL/path that’s already on the doc

     ENTERPRISE CONTRACT
     - screenshotUrl remains backward compatible and may be a path or URL.
     - screenshotPreviewPath is an in-memory helper for card renderers.
     - screenshotAssetType explicitly tells downstream URL resolvers that
       this is a public-form-image screenshot asset.
     ────────────────────────────────────────────────────────────── */
const enriched = forms.map((f) => {
  const refs = pickScreenshotRefs(f);

  return {
    ...f,

    // Preserve existing canonical fields when present; otherwise backfill
    // from asset-like contracts so downstream renderers have stable inputs.
    screenshotThumbnail: f.screenshotThumbnail ?? refs.thumb ?? null,
    screenshotPath: f.screenshotPath ?? refs.full ?? null,

    // Backward compatible: path *or* url – caller will hand it to useStorageUrl().
    screenshotUrl: refs.directRef,

    // New in-memory contract for screenshot-aware renderers.
    screenshotPreviewPath: refs.directRef,
    screenshotAssetType: 'screenshot',
    screenshotPreviewSource: refs.hasBrowserUrl
      ? 'browserUrl'
      : refs.hasStoragePath
        ? 'storagePath'
        : null,
  };
});


  // Collect the docs still missing a URL
  const unresolved = enriched
    .filter((f) => !f.screenshotUrl)
    .map((f) => f.id);

  /* Nothing left to resolve → return early */
  if (!unresolved.length) return enriched;

  /* Global templates were passed with "global" – they *should* always
     have `screenshotUrl` by now.  If not, we simply leave null (shows
     coloured fallback in <FormCard>). */
  if (organizationId === 'global' || !organizationId) {
    return enriched;
  }

console.log('[formThumbnailResolver] unresolved formIds:', unresolved);

  /* ──────────────────────────────────────────────────────────────
     2.  LEGACY FALLBACK  – look up the latest screenshot asset
         for any form still missing a URL (organisation scope only)
     ────────────────────────────────────────────────────────────── */

  const CHUNK_SIZE = 10;                    // Firestore “in” limit
  const chunks = [];
  for (let i = 0; i < unresolved.length; i += CHUNK_SIZE) {
    chunks.push(unresolved.slice(i, i + CHUNK_SIZE));
  }

  const screenshotMap = {};                 // formId → { screenshotUrl, screenshotThumbnail, screenshotPath }

for (const chunk of chunks) {
  // Track which ids we still haven't found after querying `formId`
  const remaining = new Set(chunk);

  // 2A) Query screenshots keyed by `formId`
  const qByFormId = query(
    collection(db, 'assets'),
    where('assetType',       '==', 'screenshot'),
    where('isLatestVersion', '==', true),
    where('status',          '==', 'published'),
    where('organizationId',  '==', organizationId),
    where('formId',          'in', chunk),
  );

  const snapByFormId = await getDocs(qByFormId);

  snapByFormId.forEach((doc) => {
    const d = doc.data();

    // IMPORTANT:
    // - This query is keyed by `formId`, so only `d.formId` can be used
    //   to remove entries from `remaining`.
    // - We still compute `resolvedId` to map to the best identifier we have
    //   (some docs might carry relatedFormId too).
    const hitId = d.formId || null;               // this is the field we queried by
    const resolvedId = d.formId || d.relatedFormId;

    const thumb =
      d?.thumb?.urls?.['400']
      ?? d?.thumb?.urls?.['800']
      ?? d?.versions?.thumbnail?.path
      ?? d?.versions?.thumbnail?.url
      ?? null;

    const full =
      d?.versions?.original?.path
      ?? d?.versions?.original?.url
      ?? null;

    if (resolvedId && (thumb || full)) {
      screenshotMap[resolvedId] = {
        screenshotUrl: thumb || full,
        screenshotThumbnail: thumb || null,
        screenshotPath: full || null,
      };
      if (hitId) remaining.delete(hitId);         // delete the actual queried id
    }
  });

  console.log('[formThumbnailResolver] remaining after formId query:', {
    organizationId,
    remaining: Array.from(remaining),
  });

  // 2B) If needed, query screenshots keyed by `relatedFormId`
  //     (your uploader sets relatedFormId; many docs will only have this)
  if (remaining.size > 0) {
    const remainingArr = Array.from(remaining);

    const qByRelated = query(
      collection(db, 'assets'),
      where('assetType',       '==', 'screenshot'),
      where('isLatestVersion', '==', true),
      where('status',          '==', 'published'),
      where('organizationId',  '==', organizationId),
      where('relatedFormId',   'in', remainingArr),
    );

    const snapByRelated = await getDocs(qByRelated);

    snapByRelated.forEach((doc) => {
      const d = doc.data();
      const formId = d.formId || d.relatedFormId;

      const thumb =
        d?.thumb?.urls?.['400']
        ?? d?.thumb?.urls?.['800']
        ?? d?.versions?.thumbnail?.path
        ?? d?.versions?.thumbnail?.url
        ?? null;

      const full =
        d?.versions?.original?.path
        ?? d?.versions?.original?.url
        ?? null;

      if (formId && (thumb || full)) {
        screenshotMap[formId] = {
          screenshotUrl: thumb || full,
          screenshotThumbnail: thumb || null,
          screenshotPath: full || null,
        };
      }
    });
  }
}


  /* ──────────────────────────────────────────────────────────────
     3.  MERGE & RETURN

     ENTERPRISE CONTRACT
     - Preserve existing screenshot fields when present.
     - Merge legacy asset lookup results only for missing fields.
     - Always tag the enriched object with screenshotAssetType so shared
       card renderers can call useStorageUrl with explicit policy.
     ────────────────────────────────────────────────────────────── */
  return enriched.map((f) => {
    const legacy = screenshotMap[f.id] || null;

    const screenshotUrl =
      f.screenshotUrl ||
      legacy?.screenshotUrl ||
      null;

    const screenshotThumbnail =
      f.screenshotThumbnail ||
      legacy?.screenshotThumbnail ||
      null;

    const screenshotPath =
      f.screenshotPath ||
      legacy?.screenshotPath ||
      null;

    const screenshotPreviewPath =
      screenshotThumbnail ||
      screenshotPath ||
      screenshotUrl ||
      null;

    return {
      ...f,
      screenshotUrl,
      screenshotThumbnail,
      screenshotPath,
      screenshotPreviewPath,
      screenshotAssetType: 'screenshot',
      screenshotPreviewSource:
        isBrowserUrl(screenshotPreviewPath)
          ? 'browserUrl'
          : isStorageAssetPath(screenshotPreviewPath)
            ? 'storagePath'
            : null,
    };
  });
};
